import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import TextEditable from 'common/components/TouchpointEditor/TextEditable';
import Checkmark from 'common/icons/Checkmark';
import usePrevious from 'utils/hooks/usePrevious';
import { SuiSpinner } from '@splashthat/sui';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { SessionButton } from './SessionsList.styles';

export default function SessionButtonWrapper({
    colors,
    activeFonts,
    styles,
    meta: { content },
    setContent,
    disableContentEditing,
    onClick,
    loading,
    success,
    ariaLabel,
    successMessage,
}) {
    const [showSuccess, setShowSuccess] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const prevLoading = usePrevious(loading);
    useEffect(() => {
        if (!prevLoading || loading || !success) {
            return undefined;
        }
        setShowSuccess(true);
        const timeout = setTimeout(() => {
            setShowSuccess(false);
        }, 2000);

        return () => {
            clearTimeout(timeout);
        };
    }, [loading, prevLoading, success]);

    return (
        <SessionButton
            $fontFamily={activeFonts[styles.fontFamily]}
            $fontColor={colors[styles.fontColor]}
            $fontColorHover={colors[styles.fontColorHover]}
            $fontSize={styles.fontSize}
            $backgroundColor={colors[styles.backgroundColor]}
            $backgroundColorHover={colors[styles.backgroundColorHover]}
            $borderColor={colors[styles.borderColor]}
            $borderColorHover={colors[styles.borderColorHover]}
            $borderWidth={styles.borderWidth}
            $borderRadius={styles.borderRadius}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={(e) => {
                // We need to prevent the default action of the link if editing as we want to prevent the user from
                // leaving the page. We also need to prevent the default action so that we can use textEditable.
                if (!disableContentEditing) {
                    e.preventDefault();
                }

                onClick(e);
            }}
            aria-label={ariaLabel || undefined}
        >
            <div style={{ position: 'relative', display: 'inline-block' }}>
                {/* Always render the text for sizing */}
                <span style={{ visibility: loading || showSuccess ? 'hidden' : 'visible' }}>
                    <TextEditable setContent={setContent} disabled={disableContentEditing}>
                        {content}
                    </TextEditable>
                </span>

                {loading && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        <SuiSpinner
                            size="small"
                            color={isHovered ? colors[styles.fontColorHover] : colors[styles.fontColor]}
                        />
                    </div>
                )}

                {showSuccess && !loading && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        <Checkmark aria-hidden="true" focusable="false" />
                    </div>
                )}
            </div>
            <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
                {success ? successMessage : ''}
            </VisuallyHidden>
        </SessionButton>
    );
}

SessionButtonWrapper.propTypes = {
    colors: PropTypes.array.isRequired,
    activeFonts: PropTypes.array.isRequired,
    styles: PropTypes.object.isRequired,
    meta: PropTypes.object.isRequired,
    setContent: PropTypes.func.isRequired,
    disableContentEditing: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    success: PropTypes.bool.isRequired,
    ariaLabel: PropTypes.string,
    successMessage: PropTypes.string,
};

SessionButtonWrapper.defaultProps = {
    ariaLabel: '',
    successMessage: '',
};
